// src/components/forecasting/hooks/useForecastingStateDeps.ts
//
// Adapter hook: abstracts adjacent (non-arps) Redux reads from Forecasting.tsx.
// Today: reads from Redux.
// Future: update internals when each slice migrates — Forecasting.tsx stays unchanged.
//
// @migration-note pdenSource → FocusStore when groupBySlice migrates (Priority 1)
// @migration-note syncWells → AppStore when appSlice migrates (Priority 2)
// @migration-note filterHistoryJSON → WellQueryStore when filterSlice migrates (Priority 1)
// @migration-note resetForecastZoomMode → ChartConfigStore when chartsSlice migrates (Priority 2)

import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IFilterState } from "models/filter";
import {
  SyncWells,
  filterWellsFromList,
  resetForecastZoomMode,
  setFilterState,
  setForecastWell,
  setForecastZoomModeActive,
  setSyncWells
} from "store/features";
import { RootState } from "store/rootReducer";
// eslint-disable-next-line no-restricted-imports -- intentional dual-write for cross-tab redux-state-sync; see EVA-6684
import { setSelectedWells as setSelectedWellsRedux } from "store/features/map/mapSlice";
// eslint-disable-next-line no-restricted-imports -- type-only import for the same dual-write; see EVA-6684
import type { ISelectedWell } from "store/features/map/mapSlice";
import { ForecastWell } from "components/forecasting/models/forecastWell";

export function useForecastingStateDeps() {
  const dispatch = useDispatch();

  // Adjacent Redux reads — will migrate one-by-one as their slices migrate
  const pdenSource = useSelector(
    (state: RootState) => state.groupBy.globalGroupBy.pdenSource
  );
  const syncWells = useSelector((state: RootState) => state.app.syncWells);
  const filterHistory = useSelector(
    (state: RootState) => state.filter.filterHistoryJSON
  );
  const shouldResetForecastZoomMode = useSelector(
    (state: RootState) => state.charts.resetForecastZoomMode
  );

  const dispatchSetForecastWell = useCallback(
    (forecastWell: Maybe<ForecastWell>) => dispatch(setForecastWell(forecastWell)),
    [dispatch]
  );
  const dispatchSetSyncWells = useCallback(
    (syncWellsData: SyncWells) => dispatch(setSyncWells(syncWellsData)),
    [dispatch]
  );
  const dispatchResetForecastZoomMode = useCallback(
    (value: boolean) => dispatch(resetForecastZoomMode(value)),
    [dispatch]
  );
  const dispatchSetForecastZoomModeActive = useCallback(
    (active: boolean) => dispatch(setForecastZoomModeActive(active)),
    [dispatch]
  );
  const dispatchSetFilterState = useCallback(
    (filterState: IFilterState) => dispatch(setFilterState(filterState)),
    [dispatch]
  );
  const dispatchFilterWellsFromList = useCallback(
    (payload: { wellList: string[]; resetMidstreamFlag?: boolean }) =>
      dispatch(filterWellsFromList(payload)),
    [dispatch]
  );
  // @migration-note: intentional dual-write for cross-tab sync (EVA-6684)
  // Remove when mapSlice migrates.
  const dispatchSetSelectedWellsRedux = useCallback(
    (obj: { [name: string]: ISelectedWell }) => dispatch(setSelectedWellsRedux(obj)),
    [dispatch]
  );

  return {
    pdenSource,
    syncWells,
    filterHistory,
    shouldResetForecastZoomMode,
    dispatchSetForecastWell,
    dispatchSetSyncWells,
    dispatchResetForecastZoomMode,
    dispatchSetForecastZoomModeActive,
    dispatchSetFilterState,
    dispatchFilterWellsFromList,
    dispatchSetSelectedWellsRedux
  };
}
